import type { ComponentType } from 'preact'
import { Video, Download, Calendar, PlayCircle, Megaphone } from 'lucide-preact'

export type WebTypeKey = 'OnlineEvent' | 'Download' | 'Meeting' | 'Demo'

// Lucide icon components vary slightly in their prop typing vs. standard FC definitions under Preact.
// For simplicity in this mapping and to avoid noisy type incompatibilities, we allow `any` here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconType = ComponentType<any>

// Meta includes icon plus Tailwind utility classes for the colored chip.
// We keep the styling minimal so the calling component can still add layout/spacing classes.
type Meta = { Icon: IconType; chipClass: string }

const META: Record<WebTypeKey, Meta> = {
    OnlineEvent: {
        Icon: Video,
        chipClass: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300'
    },
    Download: {
        Icon: Download,
        chipClass: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300'
    },
    Meeting: {
        Icon: Calendar,
        chipClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300'
    },
    Demo: {
        Icon: PlayCircle,
        chipClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
    },
}

const DEFAULT_META: Meta = {
    Icon: Megaphone,
    chipClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
}

/**
 * Accepts either form the API uses for a web type — the enum name ("OnlineEvent", on campaign cards)
 * or its description ("Online Event", on the web view DTO and the type lookup) — because callers get
 * one or the other depending on which endpoint fed them. Matching the enum name alone meant every
 * caller holding a description fell through to the neutral default chip without anything looking wrong.
 */
export function getWebTypeMeta(type: string | undefined | null): Meta {
    if (!type) return DEFAULT_META
    const normalized = type.replace(/\s+/g, '').toLowerCase()
    const match = (Object.keys(META) as WebTypeKey[]).find(key => key.toLowerCase() === normalized)
    return match ? META[match] : DEFAULT_META
}
